import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, Search, Menu, X, Star, ArrowRight, Truck, ShieldCheck, RotateCcw, Zap, Leaf, Sparkles, User } from "lucide-react";
import { TEMPLATES } from "./templates";
import { imgUrl } from "@/lib/api";

const Ctx = createContext(null);
export const useSF = () => useContext(Ctx);

export function StorefrontProvider({ payload, products, slug, children }) {
  const tpl = TEMPLATES[payload.template_id] || TEMPLATES.modern_luxury;
  const overrides = payload.theme || {};
  const t = useMemo(() => {
    const tok = { ...tpl.tokens };
    const c = overrides.colors || {};
    if (c.primary) tok.primary = c.primary;
    if (c.secondary) tok.secondary = c.secondary;
    if (c.accent) tok.accent = c.accent;
    if (c.button) { tok.buttonBg = c.button; }
    if (c.background) tok.bg = c.background;
    if (c.text) tok.text = c.text;
    return tok;
  }, [tpl, overrides]);
  const fonts = useMemo(() => ({ ...tpl.fonts, ...(overrides.fonts || {}) }), [tpl, overrides]);

  const sectionsMap = useMemo(() => {
    const m = {};
    for (const s of payload.sections || []) m[s.section_type] = s;
    return m;
  }, [payload]);

  const orderedSections = useMemo(() => {
    const stored = payload.sections || [];
    const known = new Set(stored.map((s) => s.section_type));
    const merged = [...stored];
    tpl.sectionOrder.forEach((st, i) => {
      if (!known.has(st)) merged.push({ section_type: st, is_visible: true, section_order: i, content: {} });
    });
    return merged.sort((a, b) => (a.section_order ?? 99) - (b.section_order ?? 99)).filter((s) => s.is_visible !== false);
  }, [payload, tpl]);

  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem(`cart_${slug}`) || "[]"));
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem(`wish_${slug}`) || "[]"));
  const [custToken, setCustToken] = useState(() => localStorage.getItem(`cust_${slug}`) || "");
  useEffect(() => localStorage.setItem(`cart_${slug}`, JSON.stringify(cart)), [cart, slug]);
  useEffect(() => localStorage.setItem(`wish_${slug}`, JSON.stringify(wishlist)), [wishlist, slug]);

  const value = {
    store: payload.store, tpl, t, fonts, sectionsMap, orderedSections,
    pages: payload.pages || {}, faqs: payload.faqs || [], nav: payload.nav || { header: [], footer: [] },
    products: products || [], slug,
    cart, setCart, wishlist, setWishlist,
    custToken,
    signInCust: (tok) => { localStorage.setItem(`cust_${slug}`, tok); setCustToken(tok); },
    signOutCust: () => { localStorage.removeItem(`cust_${slug}`); setCustToken(""); },
    addToCart: (pid, qty = 1) => setCart((c) => {
      const ex = c.find((i) => i.product_id === pid);
      return ex ? c.map((i) => (i.product_id === pid ? { ...i, qty: i.qty + qty } : i)) : [...c, { product_id: pid, qty }];
    }),
    toggleWish: (pid) => setWishlist((w) => (w.includes(pid) ? w.filter((x) => x !== pid) : [...w, pid])),
    cartCount: cart.reduce((s, i) => s + i.qty, 0),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function FontLoader({ fonts }) {
  return fonts.importUrl ? <link rel="stylesheet" href={fonts.importUrl} /> : null;
}

export function cssVars(t, fonts, tpl) {
  return {
    "--sf-bg": t.bg, "--sf-surface": t.surface, "--sf-border": t.border, "--sf-text": t.text,
    "--sf-text2": t.textSecondary, "--sf-muted": t.muted, "--sf-primary": t.primary,
    "--sf-secondary": t.secondary, "--sf-accent": t.accent, "--sf-btn": t.buttonBg,
    "--sf-btn-text": t.buttonText, "--sf-btn-hover": t.buttonHover,
    "--sf-badge-bg": t.badgeBg, "--sf-badge-text": t.badgeText,
    "--sf-radius-card": tpl.radius.card, "--sf-radius-btn": tpl.radius.button, "--sf-radius-input": tpl.radius.input,
    "--sf-font-heading": fonts.heading, "--sf-font-body": fonts.body, "--sf-font-button": fonts.button,
  };
}

export function TButton({ children, variant = "primary", className = "", ...props }) {
  const { t, tpl } = useSF();
  const base = "inline-flex items-center justify-center gap-2 transition-colors duration-300 cursor-pointer disabled:opacity-50";
  const style = variant === "primary"
    ? { background: t.buttonBg, color: t.buttonText, borderRadius: "var(--sf-radius-btn)", border: tpl.cardVariant === "pop" ? "3px solid #000" : tpl.dark ? `1px solid ${t.buttonBg}` : `1px solid ${t.buttonBg}`, fontFamily: "var(--sf-font-button)" }
    : { background: "transparent", color: t.text, borderRadius: "var(--sf-radius-btn)", border: `1px solid ${t.text}`, fontFamily: "var(--sf-font-button)" };
  return <button className={`${base} px-7 py-3 text-xs font-semibold uppercase tracking-[0.18em] hover:opacity-85 ${className}`} style={style} {...props}>{children}</button>;
}

export function Badge({ children, className = "" }) {
  const { t } = useSF();
  return <span className={`inline-block px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${className}`}
    style={{ background: t.badgeBg, color: t.badgeText, borderRadius: "var(--sf-radius-btn)" }}>{children}</span>;
}

// ---------- Product cards: 7 structurally different variants ----------
export function ProductCard({ p }) {
  const { t, tpl, addToCart, toggleWish, wishlist, slug } = useSF();
  const nav = useNavigate();
  const v = tpl.cardVariant;
  const img = imgUrl((p.images || [])[0]);
  const wished = wishlist.includes(p.id || p._id);
  const price = p.price ?? p.suggested_price;
  const mrp = p.mrp;
  const off = mrp && price && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const go = () => nav(`/store/${slug}/product/${p.id || p._id}`);
  const cartBtn = (
    <button data-testid={`add-to-cart-${p.sku}`} onClick={(e) => { e.stopPropagation(); addToCart(p.id || p._id); }}
      className="w-full py-2.5 text-[11px] font-bold uppercase tracking-widest transition-colors duration-300"
      style={{ background: t.buttonBg, color: t.buttonText, borderRadius: "var(--sf-radius-btn)", fontFamily: "var(--sf-font-button)" }}>
      Add to Cart
    </button>
  );

  if (v === "luxury") return (
    <div className="group cursor-pointer" onClick={go} data-testid={`product-card-${p.sku}`}>
      <div className="relative overflow-hidden" style={{ background: t.badgeBg }}>
        <img src={img} alt={p.name} loading="lazy" className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
        {off > 0 && <span className="absolute left-3 top-3 border px-2 py-0.5 text-[10px] tracking-widest" style={{ borderColor: t.secondary, color: t.secondary }}>-{off}%</span>}
        <div className="absolute inset-x-0 bottom-0 translate-y-full p-3 transition-transform duration-300 group-hover:translate-y-0" onClick={(e) => e.stopPropagation()}>{cartBtn}</div>
      </div>
      <div className="pt-4 text-center">
        <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: t.muted }}>{p.category || "Collection"}</p>
        <p className="mt-1 text-lg" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{p.name}</p>
        <p className="mt-1 text-sm" style={{ color: t.textSecondary }}>{fmt(price)} {mrp > price && <span className="ml-2 line-through opacity-50">{fmt(mrp)}</span>}</p>
      </div>
    </div>
  );
  if (v === "pop") return (
    <div className="group cursor-pointer rounded-3xl border-[3px] border-black bg-white p-3 shadow-[6px_6px_0px_0px_#000] transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[8px_8px_0px_0px_#000]"
      onClick={go} data-testid={`product-card-${p.sku}`}>
      <div className="relative overflow-hidden rounded-2xl">
        <img src={img} alt={p.name} loading="lazy" className="aspect-square w-full object-cover" />
        {off > 0 && <span className="absolute left-2 top-2 rounded-full border-2 border-black px-2.5 py-1 text-[10px] font-bold" style={{ background: t.secondary }}>-{off}%</span>}
        <button onClick={(e) => { e.stopPropagation(); toggleWish(p.id || p._id); }} data-testid={`wishlist-${p.sku}`}
          className="absolute right-2 top-2 rounded-full border-2 border-black bg-white p-1.5"><Heart size={13} fill={wished ? t.primary : "none"} /></button>
      </div>
      <p className="mt-3 text-sm font-bold" style={{ fontFamily: "var(--sf-font-heading)" }}>{p.name}</p>
      <div className="mt-1 flex items-center gap-2">
        <span className="rounded-full px-2.5 py-0.5 text-xs font-bold text-white" style={{ background: t.primary }}>{fmt(price)}</span>
        {mrp > price && <span className="text-xs line-through opacity-50">{fmt(mrp)}</span>}
      </div>
      <div className="mt-3" onClick={(e) => e.stopPropagation()}>{cartBtn}</div>
    </div>
  );
  if (v === "editorial") return (
    <div className="group cursor-pointer rounded-sm border p-4" style={{ borderColor: t.border, background: t.surface }} onClick={go} data-testid={`product-card-${p.sku}`}>
      <img src={img} alt={p.name} loading="lazy" className="aspect-[4/5] w-full rounded-sm object-cover transition-opacity duration-500 group-hover:opacity-90" />
      <p className="mt-4 text-lg italic" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{p.name}</p>
      <p className="mt-1 text-xs" style={{ color: t.muted }}>Crafted for everyday living</p>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-sm" style={{ color: t.text }}>{fmt(price)}</span>
        {off > 0 && <span className="rounded-full px-2 py-0.5 text-[10px] italic" style={{ background: t.badgeBg, color: t.badgeText }}>save {off}%</span>}
      </div>
      <div className="mt-3" onClick={(e) => e.stopPropagation()}>{cartBtn}</div>
    </div>
  );
  if (v === "fashion") return (
    <div className="group cursor-pointer" style={{ background: t.surface }} onClick={go} data-testid={`product-card-${p.sku}`}>
      <div className="relative overflow-hidden">
        <img src={img} alt={p.name} loading="lazy" className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {off > 0 && <span className="absolute right-0 top-4 px-2 py-1 text-[10px] font-bold text-white" style={{ background: t.primary }}>SALE</span>}
        <div className="absolute inset-x-0 bottom-0 translate-y-full transition-transform duration-300 group-hover:translate-y-0" onClick={(e) => e.stopPropagation()}>{cartBtn}</div>
      </div>
      <div className="p-3">
        <p className="text-lg uppercase tracking-wide" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{p.name}</p>
        <p className="text-sm" style={{ color: t.primary }}>{fmt(price)} {mrp > price && <span className="ml-2 text-xs line-through" style={{ color: t.muted }}>{fmt(mrp)}</span>}</p>
      </div>
    </div>
  );
  if (v === "tech") return (
    <div className="group cursor-pointer rounded-lg border p-3 backdrop-blur-xl transition-shadow duration-300 hover:shadow-[0_0_24px_rgba(0,240,255,0.25)]"
      style={{ background: t.surface, borderColor: t.border }} onClick={go} data-testid={`product-card-${p.sku}`}>
      <div className="relative overflow-hidden rounded-md">
        <img src={img} alt={p.name} loading="lazy" className="aspect-square w-full object-cover" />
        <span className="absolute left-2 top-2 flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[9px] font-bold"
          style={{ borderColor: t.primary, color: t.badgeText, background: t.badgeBg }}>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: t.accent }} />{p.available > 0 ? "IN STOCK" : "EOQ"}
        </span>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-wider" style={{ color: t.muted }}>{p.sku}</p>
      <p className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{p.name}</p>
      <p className="mt-1 font-mono text-sm" style={{ color: t.primary }}>{fmt(price)}</p>
      <div className="mt-3" onClick={(e) => e.stopPropagation()}>{cartBtn}</div>
    </div>
  );
  if (v === "organic") return (
    <div className="group cursor-pointer rounded-2xl border bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
      style={{ borderColor: t.border }} onClick={go} data-testid={`product-card-${p.sku}`}>
      <img src={img} alt={p.name} loading="lazy" className="aspect-square w-full rounded-xl object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
      <span className="mt-3 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-medium" style={{ background: t.badgeBg, color: t.badgeText }}>
        <Leaf size={10} />{p.category || "Natural"}
      </span>
      <p className="mt-2 text-base" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{p.name}</p>
      <div className="mt-1 flex items-center gap-2">
        <span className="font-semibold" style={{ color: t.secondary }}>{fmt(price)}</span>
        {mrp > price && <span className="text-xs line-through opacity-50">{fmt(mrp)}</span>}
      </div>
      <div className="mt-3" onClick={(e) => e.stopPropagation()}>{cartBtn}</div>
    </div>
  );
  return ( // playful
    <div className="group cursor-pointer rounded-3xl border-2 bg-white p-4 shadow-md transition-transform duration-300 hover:-translate-y-1 hover:rotate-1"
      style={{ borderColor: t.border }} onClick={go} data-testid={`product-card-${p.sku}`}>
      <div className="relative overflow-hidden rounded-2xl" style={{ background: t.bg }}>
        <img src={img} alt={p.name} loading="lazy" className="aspect-square w-full object-cover" />
        {off > 0 && <span className="absolute left-2 top-2 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: t.accent, color: t.badgeText }}>SAVE {off}%</span>}
        <button onClick={(e) => { e.stopPropagation(); toggleWish(p.id || p._id); }} data-testid={`wishlist-${p.sku}`}
          className="absolute right-2 top-2 rounded-full bg-white p-1.5 shadow"><Heart size={13} fill={wished ? t.primary : "none"} color={t.primary} /></button>
      </div>
      <p className="mt-3 text-base font-bold" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{p.name}</p>
      <div className="mt-1 flex items-center gap-2">
        <span className="rounded-full px-3 py-0.5 text-sm font-bold text-white" style={{ background: t.primary }}>{fmt(price)}</span>
        {mrp > price && <span className="text-xs line-through opacity-50">{fmt(mrp)}</span>}
      </div>
      <div className="mt-3" onClick={(e) => e.stopPropagation()}>{cartBtn}</div>
    </div>
  );
}

export function fmt(v) {
  return `₹${Number(v || 0).toLocaleString("en-IN")}`;
}

// ---------- Category tiles: 7 variants ----------
export function CategoryTile({ name, image, count }) {
  const { t, tpl, slug } = useSF();
  const nav = useNavigate();
  const go = () => nav(`/store/${slug}/category/${encodeURIComponent(name)}`);
  const img = image || "";
  const v = tpl.categoryVariant;
  if (v === "bubbles") return (
    <button onClick={go} data-testid={`cat-${name}`} className="group flex flex-col items-center gap-2">
      <span className="h-24 w-24 overflow-hidden rounded-full border-[3px] border-black shadow-[4px_4px_0px_0px_#000] transition-transform group-hover:-rotate-3 sm:h-28 sm:w-28"
        style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})` }}>
        {img && <img src={imgUrl(img)} alt={name} loading="lazy" className="h-full w-full object-cover" />}
      </span>
      <span className="text-xs font-bold" style={{ fontFamily: "var(--sf-font-heading)" }}>{name}</span>
    </button>
  );
  if (v === "lookbook") return (
    <button onClick={go} data-testid={`cat-${name}`} className="group relative aspect-[3/4] overflow-hidden" style={{ background: t.surface }}>
      {img && <img src={imgUrl(img)} alt={name} loading="lazy" className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />}
      <span className="absolute inset-x-0 bottom-0 p-4 text-left text-2xl uppercase tracking-wider text-white" style={{ fontFamily: "var(--sf-font-heading)", textShadow: "0 2px 12px rgba(0,0,0,0.7)" }}>{name}</span>
    </button>
  );
  if (v === "matrix") return (
    <button onClick={go} data-testid={`cat-${name}`} className="group rounded-lg border p-5 text-left backdrop-blur-xl transition-shadow hover:shadow-[0_0_20px_rgba(0,240,255,0.2)]"
      style={{ background: t.surface, borderColor: t.border }}>
      <Zap size={18} style={{ color: t.primary }} />
      <p className="mt-3 text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{name}</p>
      <p className="font-mono text-[10px]" style={{ color: t.muted }}>{count} items</p>
    </button>
  );
  if (v === "pebbles") return (
    <button onClick={go} data-testid={`cat-${name}`} className="group flex flex-col items-center gap-2">
      <span className="h-24 w-24 overflow-hidden border transition-transform group-hover:scale-105 sm:h-28 sm:w-28"
        style={{ borderColor: t.border, borderRadius: "58% 42% 55% 45% / 48% 56% 44% 52%", background: t.badgeBg }}>
        {img && <img src={imgUrl(img)} alt={name} loading="lazy" className="h-full w-full object-cover" />}
      </span>
      <span className="text-sm" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{name}</span>
    </button>
  );
  if (v === "cubbies") return (
    <button onClick={go} data-testid={`cat-${name}`} className="group rounded-3xl border-2 p-5 text-center shadow-sm transition-transform hover:-translate-y-1"
      style={{ borderColor: t.border, background: [t.accent, t.secondary, t.primary][(name.length) % 3] + "22" }}>
      <span className="text-2xl font-bold" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{name}</span>
      <p className="text-xs font-semibold" style={{ color: t.textSecondary }}>{count} products</p>
    </button>
  );
  if (v === "articles") return (
    <button onClick={go} data-testid={`cat-${name}`} className="group rounded-sm border p-5 text-left transition-colors" style={{ borderColor: t.border, background: t.surface }}>
      <p className="text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>Collection</p>
      <p className="mt-1 text-xl italic" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{name}</p>
      <p className="mt-2 flex items-center gap-1 text-xs" style={{ color: t.secondary }}>Explore <ArrowRight size={11} /></p>
    </button>
  );
  return ( // strips (luxury) + fallback
    <button onClick={go} data-testid={`cat-${name}`} className="group relative flex h-28 items-center overflow-hidden text-left" style={{ background: t.surface, border: `1px solid ${t.border}` }}>
      {img && <img src={imgUrl(img)} alt={name} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" />}
      <span className="relative ml-6 bg-white/85 px-4 py-2 text-lg tracking-wide" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{name}</span>
    </button>
  );
}

// ---------- Headers: 7 variants ----------
export function THeader() {
  const { store, t, tpl, cartCount, slug, nav, sectionsMap } = useSF();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav2 = useNavigate();
  const links = nav.header?.length ? nav.header : [
    { label: "Home", href: "" }, { label: "Shop", href: "shop" }, { label: "Track Order", href: "track" },
    { label: "FAQ", href: "faq" }, { label: "Contact", href: "contact" },
  ];
  const ann = sectionsMap.announcement;
  const annText = ann?.content?.text ?? "Free shipping across India · COD available";
  const logo = store.logo ? <img src={imgUrl(store.logo)} alt={store.name} className="h-9 w-9 rounded object-contain" /> : null;
  const name = <span className="text-xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{store.name}</span>;
  const accountBtn = (
    <Link to={`/store/${slug}/account`} data-testid="nav-account" className="p-2" style={{ color: t.text }}>
      <User size={19} />
    </Link>
  );
  const cartBtn = (
    <>
    {accountBtn}
    <Link to={`/store/${slug}/cart`} data-testid="nav-cart" className="relative p-2" style={{ color: t.text }}>
      <ShoppingBag size={19} />
      {cartCount > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold"
        style={{ background: t.primary, color: t.buttonText }}>{cartCount}</span>}
    </Link>
    </>
  );
  const searchBox = (
    <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) nav2(`/store/${slug}/search?q=${encodeURIComponent(q.trim())}`); }} className="relative">
      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: t.muted }} />
      <input data-testid="nav-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
        className="w-36 border py-1.5 pl-8 pr-2 text-xs outline-none sm:w-44"
        style={{ borderColor: t.border, background: "transparent", color: t.text, borderRadius: "var(--sf-radius-input)" }} />
    </form>
  );
  const linkEls = links.map((l) => (
    <Link key={l.label} to={`/store/${slug}${l.href ? `/${l.href}` : ""}`} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
      className="text-xs uppercase tracking-widest transition-opacity hover:opacity-70" style={{ color: t.text, fontFamily: "var(--sf-font-button)" }}>
      {l.label}
    </Link>
  ));
  const mobileMenu = open && (
    <div className="border-b px-6 py-4" style={{ borderColor: t.border, background: t.bg }}>
      <div className="flex flex-col gap-3">{linkEls}</div>
    </div>
  );
  const burger = <button data-testid="nav-burger" className="p-2 sm:hidden" style={{ color: t.text }} onClick={() => setOpen(!open)}>{open ? <X size={20} /> : <Menu size={20} />}</button>;
  const announcement = ann?.is_visible === false ? null : (
    <div data-testid="announcement-bar" className="py-2 text-center text-[11px] uppercase tracking-[0.2em]"
      style={{ background: t.announceBg, color: t.announceText, fontFamily: "var(--sf-font-button)" }}>{annText}</div>
  );

  const hv = tpl.headerVariant;
  if (hv === "centered") return (<>{announcement}
    <header className="sticky top-0 z-40 border-b px-6 py-5 backdrop-blur-md sm:px-12" style={{ background: `${t.bg}ee`, borderColor: t.border }}>
      <div className="mx-auto flex max-w-7xl items-center justify-between">
        <div className="hidden gap-8 sm:flex">{linkEls.slice(0, 2)}</div>
        {burger}
        <Link to={`/store/${slug}`} className="flex items-center gap-2">{logo}{name}</Link>
        <div className="flex items-center gap-4"><span className="hidden sm:block">{searchBox}</span><span className="hidden gap-8 sm:flex">{linkEls.slice(2, 4)}</span>{cartBtn}</div>
      </div>{mobileMenu}
    </header></>);
  if (hv === "pill") return (<>{announcement}
    <header className="sticky top-3 z-40 mx-4 rounded-full border-[3px] border-black bg-white px-6 py-3 shadow-[4px_4px_0px_0px_#000] sm:mx-8">
      <div className="flex items-center justify-between">
        <Link to={`/store/${slug}`} className="flex items-center gap-2">{logo}<span className="text-lg font-bold" style={{ fontFamily: "var(--sf-font-heading)" }}>{store.name}</span></Link>
        <div className="hidden items-center gap-5 sm:flex">{linkEls}</div>
        <div className="flex items-center gap-2">{burger}{cartBtn}</div>
      </div>{mobileMenu}
    </header></>);
  if (hv === "twotier") return (<>{announcement}
    <header className="sticky top-0 z-40 border-b px-6 py-5 sm:px-12" style={{ background: t.bg, borderColor: t.border }}>
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between">
          <p className="text-[10px] italic" style={{ color: t.muted }}>{new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })} Edition</p>
          <Link to={`/store/${slug}`} className="flex items-center gap-2">{logo}{name}</Link>
          <div className="flex items-center gap-2">{burger}{cartBtn}</div>
        </div>
        <div className="mt-4 hidden items-center justify-center gap-8 sm:flex">{linkEls}</div>
      </div>{mobileMenu}
    </header></>);
  if (hv === "stark") return (<>{announcement}
    <header className="sticky top-0 z-40 border-b px-6 py-4 sm:px-10" style={{ background: `${t.bg}f5`, borderColor: t.border }}>
      <div className="flex items-center justify-between">
        <Link to={`/store/${slug}`} className="flex items-center gap-2">{logo}<span className="text-2xl uppercase tracking-wider" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{store.name}</span></Link>
        <div className="hidden items-center gap-6 sm:flex">{linkEls}</div>
        <div className="flex items-center gap-2"><span className="hidden sm:block">{searchBox}</span>{burger}{cartBtn}</div>
      </div>{mobileMenu}
    </header></>);
  if (hv === "glass") return (<>{announcement}
    <header className="sticky top-0 z-40 border-b px-6 py-4 backdrop-blur-2xl sm:px-10" style={{ background: t.surface, borderColor: t.border }}>
      <div className="flex items-center justify-between">
        <Link to={`/store/${slug}`} className="flex items-center gap-2">{logo}<span className="text-sm font-bold uppercase tracking-widest" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{store.name}</span></Link>
        <div className="hidden items-center gap-6 sm:flex">{linkEls}</div>
        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-1.5 font-mono text-[10px] sm:flex" style={{ color: t.accent }}>
            <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: t.accent }} />ONLINE</span>
          {burger}{cartBtn}
        </div>
      </div>{mobileMenu}
    </header></>);
  if (hv === "sage") return (<>{announcement}
    <header className="sticky top-0 z-40 border-b px-6 py-4 backdrop-blur-md sm:px-10" style={{ background: `${t.bg}ee`, borderColor: t.border }}>
      <div className="mx-auto flex max-w-6xl items-center justify-between">
        <Link to={`/store/${slug}`} className="flex items-center gap-2">{logo}{name}</Link>
        <div className="hidden items-center gap-2 sm:flex">{links.map((l) => (
          <Link key={l.label} to={`/store/${slug}${l.href ? `/${l.href}` : ""}`} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="rounded-full px-4 py-1.5 text-sm transition-colors" style={{ color: t.text }}
            onMouseEnter={(e) => (e.target.style.background = t.badgeBg)} onMouseLeave={(e) => (e.target.style.background = "transparent")}>{l.label}</Link>
        ))}</div>
        <div className="flex items-center gap-2">{burger}{cartBtn}</div>
      </div>{mobileMenu}
    </header></>);
  return (<>{announcement}
    <header className="sticky top-0 z-40 border-b-2 bg-white px-6 py-4 shadow-sm sm:px-10" style={{ borderColor: t.border }}>
      <div className="flex items-center justify-between">
        <Link to={`/store/${slug}`} className="flex items-center gap-2">{logo}<span className="text-xl font-bold" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{store.name}</span></Link>
        <div className="hidden items-center gap-5 sm:flex">{linkEls}</div>
        <div className="flex items-center gap-2">{burger}{cartBtn}</div>
      </div>{mobileMenu}
    </header></>);
}

// ---------- Footers: 7 variants ----------
export function TFooter() {
  const { store, t, tpl, slug, pages, nav } = useSF();
  const groups = nav.footer?.length ? nav.footer : [
    { group: "Shop", links: [{ label: "All Products", href: "shop" }, { label: "Track Order", href: "track" }] },
    { group: "Customer Care", links: [{ label: "My Account", href: "account" }, { label: "Contact", href: "contact" }, { label: "FAQ", href: "faq" }, { label: "Shipping", href: "shipping" }, { label: "Returns", href: "returns" }] },
    { group: "Company", links: [{ label: "About Us", href: "about" }, { label: "Blog", href: "blog" }] },
    { group: "Legal", links: [{ label: "Privacy Policy", href: "privacy" }, { label: "Terms", href: "terms" }, { label: "Refunds", href: "refunds" }, { label: "Cancellation", href: "cancellation" }] },
  ];
  const visibleGroups = groups.map((g) => ({
    ...g, links: g.links.filter((l) => !["about", "faq", "shipping", "returns", "refunds", "privacy", "terms", "cancellation", "blog", "contact"].includes(l.href) || pages[l.href]?.is_visible !== false),
  }));
  const fv = tpl.footerVariant;
  const socials = store.social || {};
  return (
    <footer className="mt-20" style={{ background: t.footerBg, color: t.footerText }} data-testid="store-footer">
      {fv === "ticker" && (
        <div className="overflow-hidden border-b py-3" style={{ borderColor: t.border }}>
          <p className="whitespace-nowrap text-2xl uppercase tracking-widest opacity-20" style={{ fontFamily: "var(--sf-font-heading)" }}>
            {Array(6).fill(`${store.name} · FREE SHIPPING · COD AVAILABLE · `).join("")}
          </p>
        </div>
      )}
      {fv === "terminal" && (
        <div className="border-b px-6 py-3 font-mono text-[10px] sm:px-12" style={{ borderColor: t.border, color: t.accent }}>
          <span className="mr-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: t.accent }} />
          ALL SYSTEMS OPERATIONAL · SECURE CHECKOUT · {new Date().toISOString().slice(0, 10)}
        </div>
      )}
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 sm:grid-cols-2 sm:px-12 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <p className="text-2xl" style={{ fontFamily: "var(--sf-font-heading)" }}>{store.name}</p>
          <p className="mt-3 max-w-xs text-sm opacity-70">{store.tagline || store.description || "Quality products, delivered fast across India."}</p>
          {store.contact?.email && <p className="mt-3 text-xs opacity-70">{store.contact.email}{store.contact.mobile ? ` · ${store.contact.mobile}` : ""}</p>}
          {store.contact?.address && <p className="mt-1 text-xs opacity-70">{store.contact.address}</p>}
          {Object.keys(socials).length > 0 && (
            <div className="mt-4 flex gap-3">
              {Object.entries(socials).filter(([, v]) => v).map(([k, v]) => (
                <a key={k} href={v} target="_blank" rel="noreferrer" data-testid={`social-${k}`}
                  className="rounded-full border px-3 py-1 text-[10px] uppercase tracking-wider opacity-80 transition-opacity hover:opacity-100"
                  style={{ borderColor: t.footerAccent }}>{k}</a>
              ))}
            </div>
          )}
        </div>
        {visibleGroups.map((g) => (
          <div key={g.group}>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: t.footerAccent }}>{g.group}</p>
            <div className="mt-4 flex flex-col gap-2.5">
              {g.links.map((l) => (
                <Link key={l.label} to={`/store/${slug}/${l.href}`} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm opacity-70 transition-opacity hover:opacity-100">{l.label}</Link>
              ))}
            </div>
          </div>
        ))}
      </div>
      {fv === "watermark" && (
        <p className="select-none overflow-hidden whitespace-nowrap text-center text-[18vw] font-light uppercase leading-none opacity-5"
          style={{ fontFamily: "var(--sf-font-heading)" }}>{store.name}</p>
      )}
      <div className="border-t px-6 py-5 text-center text-[10px] uppercase tracking-widest opacity-50" style={{ borderColor: `${t.footerText}22` }}>
        © {new Date().getFullYear()} {store.name} · Powered by DropPartner
      </div>
    </footer>
  );
}

// ---------- Heroes: 7 layouts ----------
export function HeroSection() {
  const { store, t, tpl, sectionsMap, slug } = useSF();
  const c = sectionsMap.hero?.content || {};
  const heading = c.heading || store.tagline || `Welcome to ${store.name}`;
  const sub = c.subheading || "Handpicked quality products, delivered fast across India.";
  const ctaText = c.cta_text || "Shop Now";
  const ctaUrl = c.cta_url || "shop";
  const img = c.image ? imgUrl(c.image) : null;
  const badge = c.badge || "";
  const nav = useNavigate();
  const go = () => nav(`/store/${slug}/${ctaUrl.replace(/^\//, "")}`);
  const layout = sectionsMap.hero?.content?.layout || tpl.heroLayout;

  if (layout === "split-editorial") return (
    <section data-testid="hero-section" className="mx-auto grid max-w-7xl gap-12 px-6 py-20 sm:px-12 lg:grid-cols-12 lg:py-32">
      <div className="lg:col-span-7">{img && <div className="relative"><img src={img} alt="" className="aspect-[3/4] w-full object-cover" />
        <span className="absolute inset-4 border" style={{ borderColor: t.secondary }} /></div>}</div>
      <div className="flex flex-col justify-center lg:col-span-5">
        {badge && <p className="mb-6 text-[10px] uppercase tracking-[0.35em]" style={{ color: t.secondary }}>{badge}</p>}
        <h1 className="text-5xl font-light leading-tight tracking-wide sm:text-6xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{heading}</h1>
        <p className="mt-6 max-w-md text-sm leading-relaxed" style={{ color: t.textSecondary }}>{sub}</p>
        <button onClick={go} data-testid="hero-cta" className="mt-10 inline-flex w-fit items-center gap-3 border-b pb-2 text-xs uppercase tracking-[0.25em] transition-colors"
          style={{ borderColor: t.text, color: t.text, fontFamily: "var(--sf-font-button)" }}>{ctaText} <ArrowRight size={13} /></button>
      </div>
    </section>);
  if (layout === "bento") return (
    <section data-testid="hero-section" className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-3xl border-[3px] border-black p-8 shadow-[6px_6px_0px_0px_#000] lg:col-span-2" style={{ background: t.primary }}>
          {badge && <span className="mb-4 inline-block -rotate-2 rounded-full border-2 border-black px-3 py-1 text-xs font-bold" style={{ background: t.secondary }}>{badge}</span>}
          <h1 className="text-4xl font-bold text-white sm:text-6xl" style={{ fontFamily: "var(--sf-font-heading)" }}>{heading}</h1>
          <p className="mt-4 max-w-md font-medium text-white/90">{sub}</p>
          <button onClick={go} data-testid="hero-cta" className="mt-8 rounded-full border-[3px] border-black px-8 py-3 text-sm font-bold shadow-[4px_4px_0px_0px_#000] transition-all hover:-translate-y-0.5"
            style={{ background: t.secondary, color: t.text }}>{ctaText}</button>
        </div>
        <div className="overflow-hidden rounded-3xl border-[3px] border-black shadow-[6px_6px_0px_0px_#000]">
          {img ? <img src={img} alt="" className="h-full min-h-64 w-full object-cover" /> : <div className="flex h-full min-h-64 items-center justify-center text-6xl" style={{ background: t.accent }}><Sparkles size={56} /></div>}
        </div>
      </div>
    </section>);
  if (layout === "magazine") return (
    <section data-testid="hero-section" className="mx-auto max-w-6xl px-6 py-16 sm:px-12 lg:py-24">
      <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: t.muted }}>{badge || "Latest Collection"}</p>
      <div className="mt-4 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5 lg:pt-16">
          <h1 className="text-4xl italic leading-tight sm:text-5xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{heading}</h1>
          <p className="mt-6 max-w-sm text-sm leading-relaxed" style={{ color: t.textSecondary }}>{sub}</p>
          <button onClick={go} data-testid="hero-cta" className="mt-8 text-xs uppercase tracking-[0.2em] underline underline-offset-8 transition-colors"
            style={{ color: t.primary, fontFamily: "var(--sf-font-button)" }}>{ctaText}</button>
        </div>
        <div className="lg:col-span-7">{img && <img src={img} alt="" className="aspect-[4/5] w-full rounded-sm object-cover lg:ml-16 lg:w-4/5" />}</div>
      </div>
    </section>);
  if (layout === "fullbleed") return (
    <section data-testid="hero-section" className="relative">
      <div className="relative min-h-[70vh] overflow-hidden">
        {img && <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />}
        <div className="relative flex min-h-[70vh] flex-col justify-end p-6 sm:p-12">
          {badge && <span className="mb-4 w-fit px-3 py-1 text-xs font-bold uppercase tracking-widest text-white" style={{ background: t.primary }}>{badge}</span>}
          <h1 className="max-w-4xl text-6xl uppercase leading-none sm:text-8xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{heading}</h1>
          <p className="mt-4 max-w-lg text-sm" style={{ color: t.textSecondary }}>{sub}</p>
          <button onClick={go} data-testid="hero-cta" className="mt-8 w-fit px-10 py-3 text-lg uppercase tracking-widest transition-colors"
            style={{ background: t.buttonBg, color: t.buttonText, fontFamily: "var(--sf-font-button)" }}>{ctaText}</button>
        </div>
      </div>
      <div className="overflow-hidden border-y py-2.5" style={{ borderColor: t.border, background: t.surface }}>
        <p className="whitespace-nowrap text-sm uppercase tracking-[0.3em]" style={{ color: t.primary, fontFamily: "var(--sf-font-heading)" }}>
          {Array(8).fill(" NEW DROP · LIMITED STOCK · FREE SHIPPING ·").join("")}
        </p>
      </div>
    </section>);
  if (layout === "hud") return (
    <section data-testid="hero-section" className="mx-auto grid max-w-7xl gap-8 px-6 py-16 sm:px-10 lg:grid-cols-12">
      <div className="flex flex-col justify-center lg:col-span-6">
        <p className="flex items-center gap-2 font-mono text-[10px]" style={{ color: t.accent }}>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: t.accent }} />{badge || "SYSTEM.ONLINE"}</p>
        <h1 className="mt-4 text-4xl font-black uppercase tracking-wider sm:text-5xl"
          style={{ fontFamily: "var(--sf-font-heading)", color: t.primary, textShadow: `0 0 18px ${t.primary}66` }}>{heading}</h1>
        <p className="mt-5 max-w-md font-mono text-xs leading-relaxed" style={{ color: t.textSecondary }}>{sub}</p>
        <button onClick={go} data-testid="hero-cta" className="mt-8 w-fit rounded-md px-8 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-all"
          style={{ background: t.buttonBg, color: t.buttonText, boxShadow: `0 0 15px ${t.primary}80`, fontFamily: "var(--sf-font-button)" }}>{ctaText}</button>
      </div>
      <div className="relative lg:col-span-6">
        {img && <img src={img} alt="" className="w-full rounded-lg border object-cover" style={{ borderColor: t.border, boxShadow: `0 0 30px ${t.primary}22` }} />}
        <div className="absolute right-3 top-3 rounded-md border px-3 py-2 font-mono text-[9px] backdrop-blur-xl" style={{ borderColor: t.border, background: t.surface, color: t.accent }}>
          STOCK: LIVE<br />SHIPPING: 24H<br />COD: ENABLED</div>
      </div>
    </section>);
  if (layout === "arch") return (
    <section data-testid="hero-section" className="mx-auto max-w-6xl px-6 py-16 sm:px-12">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div>
          {badge && <span className="mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold" style={{ background: t.badgeBg, color: t.badgeText }}><Leaf size={11} />{badge}</span>}
          <h1 className="text-4xl leading-tight sm:text-5xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{heading}</h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed" style={{ color: t.textSecondary }}>{sub}</p>
          <button onClick={go} data-testid="hero-cta" className="mt-8 rounded-full px-8 py-3.5 text-sm font-medium text-white transition-colors"
            style={{ background: t.buttonBg, fontFamily: "var(--sf-font-button)" }}>{ctaText}</button>
        </div>
        {img && <div className="overflow-hidden border" style={{ borderColor: t.border, borderRadius: "999px 999px 16px 16px" }}>
          <img src={img} alt="" className="aspect-[4/5] w-full object-cover" /></div>}
      </div>
    </section>);
  return ( // storybook (playful)
    <section data-testid="hero-section" className="relative" style={{ background: t.primary }}>
      <div className="mx-auto grid max-w-6xl items-center gap-8 px-6 pb-24 pt-14 sm:px-12 lg:grid-cols-2">
        <div>
          {badge && <span className="mb-4 inline-block -rotate-2 rounded-full px-4 py-1.5 text-xs font-bold" style={{ background: t.accent, color: t.badgeText }}>{badge}</span>}
          <h1 className="text-4xl font-bold text-white sm:text-6xl" style={{ fontFamily: "var(--sf-font-heading)" }}>{heading}</h1>
          <p className="mt-4 max-w-md font-semibold text-white/90">{sub}</p>
          <button onClick={go} data-testid="hero-cta" className="mt-8 rounded-full px-8 py-3.5 text-base font-bold shadow-lg transition-transform hover:-translate-y-0.5"
            style={{ background: t.secondary, color: "#fff", fontFamily: "var(--sf-font-button)" }}>{ctaText}</button>
        </div>
        {img && <img src={img} alt="" className="w-full rounded-3xl border-4 border-white object-cover shadow-xl" />}
      </div>
      <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ height: 40 }}>
        <path d="M0,30 C240,60 480,0 720,30 C960,60 1200,0 1440,30 L1440,60 L0,60 Z" fill={t.bg} />
      </svg>
    </section>);
}

// ---------- Promo banners: 7 variants ----------
export function PromoBanner() {
  const { t, tpl, sectionsMap, slug } = useSF();
  const c = sectionsMap.promo_banner?.content || {};
  const heading = c.heading || "Special Offer";
  const sub = c.subheading || "Limited time deals on selected products";
  const ctaText = c.cta_text || "Shop the Offer";
  const nav = useNavigate();
  const img = c.image ? imgUrl(c.image) : null;
  const go = () => nav(`/store/${slug}/${(c.cta_url || "shop").replace(/^\//, "")}`);
  const v = tpl.bannerVariant;

  if (v === "mono-gold") return (
    <section data-testid="promo-banner" className="my-8" style={{ background: t.primary }}>
      <div className="mx-auto max-w-4xl px-6 py-20 text-center">
        <h2 className="text-4xl italic sm:text-5xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.secondary }}>{heading}</h2>
        <p className="mt-4 text-sm tracking-wide" style={{ color: t.buttonText }}>{sub}</p>
        <button onClick={go} data-testid="promo-cta" className="mt-8 border px-8 py-3 text-xs uppercase tracking-[0.25em] transition-colors"
          style={{ borderColor: t.secondary, color: t.secondary }}>{ctaText}</button>
      </div>
    </section>);
  if (v === "sticker") return (
    <section data-testid="promo-banner" className="mx-auto my-10 max-w-6xl px-4">
      <div className="relative overflow-hidden rounded-3xl border-[3px] border-black p-10 shadow-[8px_8px_0px_0px_#000]" style={{ background: t.secondary }}>
        <span className="absolute -right-4 -top-4 rotate-12 rounded-full border-2 border-black px-4 py-2 text-xs font-bold text-white" style={{ background: t.primary }}>HOT DEAL</span>
        <h2 className="max-w-lg text-4xl font-bold sm:text-5xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{heading}</h2>
        <p className="mt-3 max-w-md font-medium" style={{ color: t.text }}>{sub}</p>
        <button onClick={go} data-testid="promo-cta" className="mt-6 rounded-full border-[3px] border-black bg-white px-8 py-3 text-sm font-bold shadow-[4px_4px_0px_0px_#000] transition-all hover:-translate-y-0.5">{ctaText}</button>
        {img && <img src={img} alt="" className="absolute bottom-0 right-8 hidden w-56 rounded-t-2xl border-[3px] border-b-0 border-black object-cover lg:block" />}
      </div>
    </section>);
  if (v === "linen") return (
    <section data-testid="promo-banner" className="mx-auto my-10 max-w-5xl px-6">
      <div className="border p-2" style={{ borderColor: t.border }}>
        <div className="border px-6 py-16 text-center" style={{ borderColor: t.border, background: t.surface }}>
          <h2 className="text-3xl italic sm:text-4xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>“{heading}”</h2>
          <p className="mx-auto mt-4 max-w-md text-sm" style={{ color: t.textSecondary }}>{sub}</p>
          <button onClick={go} data-testid="promo-cta" className="mt-7 text-xs uppercase tracking-[0.2em] underline underline-offset-8" style={{ color: t.primary }}>{ctaText}</button>
        </div>
      </div>
    </section>);
  if (v === "drop") return (
    <section data-testid="promo-banner" className="my-8" style={{ background: t.primary }}>
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-6 px-6 py-14 sm:px-12">
        <div>
          <h2 className="text-5xl uppercase leading-none text-white sm:text-7xl" style={{ fontFamily: "var(--sf-font-heading)" }}>{heading}</h2>
          <p className="mt-3 text-sm uppercase tracking-widest text-white/80">{sub}</p>
        </div>
        <button onClick={go} data-testid="promo-cta" className="px-10 py-4 text-lg uppercase tracking-widest transition-colors"
          style={{ background: t.buttonBg, color: t.buttonText, fontFamily: "var(--sf-font-button)" }}>{ctaText}</button>
      </div>
    </section>);
  if (v === "cyber") return (
    <section data-testid="promo-banner" className="mx-auto my-10 max-w-6xl px-6">
      <div className="relative overflow-hidden rounded-lg border p-10" style={{ borderColor: t.secondary, background: `linear-gradient(120deg, ${t.bg}, #1A0B36)` }}>
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: t.primary, boxShadow: `0 0 12px ${t.primary}` }} />
        <p className="font-mono text-[10px]" style={{ color: t.accent }}>// LIMITED_TIME_EVENT</p>
        <h2 className="mt-3 text-3xl font-black uppercase tracking-wider sm:text-4xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.primary, textShadow: `0 0 16px ${t.primary}55` }}>{heading}</h2>
        <p className="mt-3 font-mono text-xs" style={{ color: t.textSecondary }}>{sub}</p>
        <button onClick={go} data-testid="promo-cta" className="mt-6 rounded-md px-7 py-3 text-xs font-bold uppercase tracking-[0.2em]"
          style={{ background: t.buttonBg, color: t.buttonText, boxShadow: `0 0 15px ${t.primary}66` }}>{ctaText}</button>
        {img && <img src={img} alt="" className="absolute bottom-0 right-0 hidden w-64 object-cover opacity-70 lg:block" />}
      </div>
    </section>);
  if (v === "earth") return (
    <section data-testid="promo-banner" className="my-10" style={{ background: t.secondary }}>
      <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="block w-full" style={{ height: 28 }}><path d="M0,40 C360,0 1080,0 1440,40 L1440,40 L0,40 Z" fill={t.bg} transform="translate(0,-2) rotate(180 720 20)" /></svg>
      <div className="mx-auto max-w-3xl px-6 py-14 text-center">
        <h2 className="text-3xl sm:text-4xl" style={{ fontFamily: "var(--sf-font-heading)", color: "#FFF8F0" }}>{heading}</h2>
        <p className="mt-3 text-sm text-white/85">{sub}</p>
        <button onClick={go} data-testid="promo-cta" className="mt-7 rounded-full bg-white px-8 py-3 text-sm font-medium transition-colors" style={{ color: t.primary }}>{ctaText}</button>
      </div>
      <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="block w-full" style={{ height: 28 }}><path d="M0,0 C360,40 1080,40 1440,0 L1440,0 L0,0 Z" fill={t.bg} /></svg>
    </section>);
  return ( // club (playful)
    <section data-testid="promo-banner" className="mx-auto my-10 max-w-6xl px-4">
      <div className="relative overflow-hidden rounded-3xl p-10 text-center" style={{ background: t.secondary }}>
        <span className="absolute left-6 top-6 text-2xl">★</span><span className="absolute bottom-6 right-8 text-2xl">★</span>
        <h2 className="text-3xl font-bold text-white sm:text-5xl" style={{ fontFamily: "var(--sf-font-heading)" }}>{heading}</h2>
        <p className="mt-3 font-semibold text-white/90">{sub}</p>
        <button onClick={go} data-testid="promo-cta" className="mt-6 rounded-full px-8 py-3 text-sm font-bold shadow-lg transition-transform hover:-translate-y-0.5"
          style={{ background: t.accent, color: t.badgeText }}>{ctaText}</button>
      </div>
    </section>);
}

// ---------- Section renderer ----------
export function SectionShell({ title, sub, children, testid }) {
  const { t } = useSF();
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-10 sm:py-20" data-testid={testid}>
      {title && <h2 className="text-2xl sm:text-4xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{title}</h2>}
      {sub && <p className="mt-2 max-w-xl text-sm" style={{ color: t.textSecondary }}>{sub}</p>}
      <div className="mt-8">{children}</div>
    </section>
  );
}

export function TrustStrip() {
  const { t, tpl } = useSF();
  const items = tpl.dark
    ? [[Zap, "24H Dispatch"], [ShieldCheck, "Secure Payments"], [Truck, "Pan-India Delivery"], [RotateCcw, "Easy Returns"]]
    : [[Truck, "Fast Delivery"], [ShieldCheck, "Secure Payments"], [RotateCcw, "Easy Returns"], [Star, "Quality Assured"]];
  return (
    <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-6 sm:grid-cols-4" data-testid="trust-strip">
      {items.map(([Icon, label]) => (
        <div key={label} className="flex items-center gap-3 border p-4" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }}>
          <Icon size={18} style={{ color: t.primary }} />
          <span className="text-xs font-semibold" style={{ color: t.text }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

export function ProductGrid({ items, testid }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-4" data-testid={testid}>
      {items.map((p) => <ProductCard key={p.id || p._id} p={p} />)}
    </div>
  );
}

export function SectionRenderer() {
  const { orderedSections, sectionsMap, products } = useSF();
  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))];
  const catCounts = categories.reduce((m, c) => ({ ...m, [c]: products.filter((p) => p.category === c).length }), {});
  const sec = (type) => sectionsMap[type]?.content || {};
  return (
    <>
      {orderedSections.map((s) => {
        const c = s.content || {};
        switch (s.section_type) {
          case "hero": return <HeroSection key="hero" />;
          case "categories":
            return categories.length ? (
              <SectionShell key="cat" title={c.heading || "Shop by Category"} testid="section-categories">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {categories.slice(0, 8).map((cat) => <CategoryTile key={cat} name={cat} image={sec("categories").category_images?.[cat]} count={catCounts[cat]} />)}
                </div>
              </SectionShell>) : null;
          case "featured_products":
          case "featured_collection":
            return <SectionShell key={s.section_type} title={c.heading || (s.section_type === "featured_collection" ? "Featured Collection" : "Featured Products")} sub={c.subheading} testid={`section-${s.section_type}`}>
              <ProductGrid items={products.slice(0, 8)} testid="featured-grid" /></SectionShell>;
          case "new_arrivals":
            return <SectionShell key="na" title={c.heading || "New Arrivals"} testid="section-new-arrivals">
              <ProductGrid items={[...products].reverse().slice(0, 4)} testid="new-arrivals-grid" /></SectionShell>;
          case "flash_deals":
            return <SectionShell key="fd" title={c.heading || "Flash Deals"} sub={c.ends_text} testid="section-flash-deals">
              <ProductGrid items={products.filter((p) => p.mrp > (p.price ?? p.suggested_price)).slice(0, 4)} testid="deals-grid" /></SectionShell>;
          case "promo_banner": return <PromoBanner key="promo" />;
          case "best_sellers":
            return <SectionShell key="bs" title={c.heading || "Best Sellers"} testid="section-best-sellers">
              <ProductGrid items={products.slice(0, 4)} testid="bestsellers-grid" /></SectionShell>;
          case "lifestyle":
            return (
              <SectionShell key="ls" title={c.heading} testid="section-lifestyle">
                <div className="grid items-center gap-8 lg:grid-cols-2">
                  {c.image && <img src={imgUrl(c.image)} alt="" loading="lazy" className="w-full object-cover" style={{ borderRadius: "var(--sf-radius-card)" }} />}
                  <p className="text-sm leading-relaxed" style={{ color: "var(--sf-text2)" }}>{c.text || ""}</p>
                </div>
              </SectionShell>);
          case "reviews": return <ReviewsSection key="rev" content={c} />;
          case "why_us": return <TrustStrip key="trust" />;
          case "instagram_gallery":
            return c.images?.length ? (
              <SectionShell key="ig" title={c.heading || "From Our World"} testid="section-gallery">
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">{c.images.slice(0, 6).map((im, i) => <img key={i} src={imgUrl(im)} alt="" loading="lazy" className="aspect-square w-full object-cover" style={{ borderRadius: "var(--sf-radius-card)" }} />)}</div>
              </SectionShell>) : null;
          case "newsletter": return <NewsletterSection key="nl" content={c} />;
          default: return null;
        }
      })}
    </>
  );
}

export function ReviewsSection({ content }) {
  const { t } = useSF();
  const reviews = content.reviews?.length ? content.reviews : [
    { name: "Priya S.", text: "Great quality and super fast delivery. Will order again!", rating: 5 },
    { name: "Rahul M.", text: "Exactly as described. Smooth COD experience.", rating: 5 },
    { name: "Ananya K.", text: "Beautiful packaging and genuine products.", rating: 4 },
  ];
  return (
    <SectionShell title={content.heading || "What Our Customers Say"} testid="section-reviews">
      <div className="grid gap-4 sm:grid-cols-3">
        {reviews.slice(0, 3).map((r, i) => (
          <div key={i} className="border p-6" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }}>
            <div className="flex gap-0.5">{Array.from({ length: r.rating || 5 }).map((_, j) => <Star key={j} size={12} fill={t.secondary} color={t.secondary} />)}</div>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: t.text }}>“{r.text}”</p>
            <p className="mt-3 text-xs font-semibold" style={{ color: t.muted }}>— {r.name}</p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

export function NewsletterSection({ content }) {
  const { t } = useSF();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  return (
    <SectionShell testid="section-newsletter">
      <div className="mx-auto max-w-xl border p-10 text-center" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)", background: t.surface }}>
        <h3 className="text-2xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{content.heading || "Stay in the loop"}</h3>
        <p className="mt-2 text-sm" style={{ color: t.textSecondary }}>{content.subheading || "New arrivals, offers and stories — straight to your inbox."}</p>
        {done ? <p className="mt-6 text-sm font-semibold" style={{ color: t.primary }} data-testid="newsletter-success">You're on the list!</p> : (
          <form className="mt-6 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (email.includes("@")) setDone(true); }}>
            <input data-testid="newsletter-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
              className="flex-1 border bg-transparent px-4 py-2.5 text-sm outline-none" style={{ borderColor: t.border, color: t.text, borderRadius: "var(--sf-radius-input)" }} />
            <TButton type="submit" data-testid="newsletter-submit" className="!px-5">Join</TButton>
          </form>)}
      </div>
    </SectionShell>
  );
}

export function EmptyBlock({ title, sub, testid }) {
  const { t } = useSF();
  return (
    <div className="mx-auto max-w-md border p-12 text-center" style={{ borderColor: t.border, borderRadius: "var(--sf-radius-card)" }} data-testid={testid}>
      <p className="text-2xl" style={{ fontFamily: "var(--sf-font-heading)", color: t.text }}>{title}</p>
      {sub && <p className="mt-2 text-sm" style={{ color: t.textSecondary }}>{sub}</p>}
    </div>
  );
}
